/**
 * The business's own view: who is on what, what they have spent, and what it cost us (Phase 7).
 *
 * Everything here is read except one button. Granting credit is what you need when a run fails on
 * our side or a charge is disputed, and it goes through the ledger with the reason attached, so it
 * shows up in the customer's own history rather than as a balance that silently changed.
 *
 * The endpoints answer 404 to anyone not named in the deployment's admin list, so this page is not
 * the security boundary - it only avoids offering a door that will not open.
 */

import { useState } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useAdminOrgs,
  useAdminOverview,
  useDeleteOrg,
  useDeletionPreview,
  useGrantCredit,
  useKillSwitch,
  useMe,
  useSetKillSwitch,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

const money = (usd: number) => `$${usd.toFixed(2)}`;

export function AdminPage() {
  const me = useMe();
  const isAdmin = me.data?.is_admin === true;
  const overview = useAdminOverview(isAdmin);
  const orgs = useAdminOrgs(isAdmin);
  const [granting, setGranting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  if (me.isLoading) {
    return <section className="mx-auto max-w-6xl px-6 py-10 text-sm text-muted-foreground">Loading…</section>;
  }
  if (!isAdmin) {
    return (
      <section className="mx-auto max-w-2xl px-6 py-10">
        <Alert>
          <AlertDescription>This page is not available to your account.</AlertDescription>
        </Alert>
      </section>
    );
  }

  const seen = overview.data;
  return (
    <section className="mx-auto max-w-6xl space-y-6 bg-[rgb(15_17_21/0.88)] px-6 py-10 backdrop-blur-sm">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Admin</h1>
        <p className="text-sm text-muted-foreground">
          Every organisation on this deployment. Figures are in US dollars, from the credit ledger and
          recorded model usage.
        </p>
      </header>

      <KillSwitch />

      {seen && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Figure label="Organisations" value={String(seen.orgs)} note={`${seen.paying} paying`} />
          <Figure label="Past due" value={String(seen.past_due)} note="live runs paused" />
          <Figure label="Credit outstanding" value={money(seen.outstanding_usd)}
                  note="granted, not yet spent or expired" />
          <Figure label="Margin so far" value={money(seen.margin_usd)}
                  note={`${money(seen.charged_usd)} charged, ${money(seen.cost_usd)} cost, ×${seen.markup} markup`} />
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-[#16181d] text-left text-xs uppercase text-muted-foreground">
            <tr>
              {["Organisation", "Plan", "State", "Credit", "Granted", "Charged", "Cost", "Margin", ""].map((h) => (
                <th key={h} className="px-3 py-2 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(orgs.data ?? []).map((org) => (
              <tr key={org.id} className="border-t border-white/5">
                <td className="px-3 py-2">
                  <div>{org.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    since {new Date(org.created_at).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-3 py-2">{org.plan}</td>
                <td className={cn("px-3 py-2", org.status === "past_due" && "text-step-todo")}>
                  {org.status}
                </td>
                <td className="px-3 py-2 tabular-nums">
                  {money(org.credit_available_usd)}
                  {org.credit_held_usd > 0 && (
                    <span className="text-[11px] text-muted-foreground"> +{money(org.credit_held_usd)} held</span>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums">{money(org.granted_usd)}</td>
                <td className="px-3 py-2 tabular-nums">{money(org.charged_usd)}</td>
                <td className="px-3 py-2 tabular-nums">{money(org.cost_usd)}</td>
                <td className={cn("px-3 py-2 tabular-nums", org.margin_usd < 0 && "text-step-todo")}>
                  {money(org.margin_usd)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setGranting(org.id)}>Credit…</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(org.id)}>Delete…</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {orgs.data?.length === 0 && (
        <p className="text-sm text-muted-foreground">No organisations yet.</p>
      )}

      {granting && <GrantDialog orgId={granting} onClose={() => setGranting(null)} />}
      {deleting && <DeleteDialog orgId={deleting} onClose={() => setDeleting(null)} />}
    </section>
  );
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#16181d] p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {note && <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>}
    </div>
  );
}

function GrantDialog({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const grant = useGrantCredit();
  const [usd, setUsd] = useState("");
  const [reason, setReason] = useState("");
  const amount = Number(usd);
  const ready = amount > 0 && reason.trim().length >= 3;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm space-y-3 rounded-xl border border-white/10 bg-[#16181d] p-4">
        <h2 className="font-medium">Grant credit</h2>
        <p className="text-xs text-muted-foreground">
          The reason is shown to the customer in their own billing history, with your address.
        </p>
        <Input inputMode="decimal" placeholder="Amount in dollars" value={usd}
               onChange={(e) => setUsd(e.target.value)} />
        <Input placeholder="Reason, e.g. a run that failed on our side" value={reason}
               onChange={(e) => setReason(e.target.value)} />
        {grant.isError && (
          <Alert variant="destructive">
            <AlertDescription>That could not be granted. Check the amount and try again.</AlertDescription>
          </Alert>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!ready || grant.isPending}
            onClick={() => grant.mutate({ orgId, usd: amount, reason: reason.trim() },
                                        { onSuccess: onClose })}
          >
            Grant {ready ? money(amount) : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Stop paid work across the service, and see what is being spent while deciding.
 *
 * Deliberately the first thing on the page and impossible to throw by accident: pausing needs a
 * reason typed in, because the reason is what you will want at three in the morning next time.
 */
function KillSwitch() {
  const me = useMe();
  const state = useKillSwitch(me.data?.is_admin === true);
  const set = useSetKillSwitch();
  const [reason, setReason] = useState("");
  const view = state.data;
  if (!view) return null;

  const spendShare = view.cap_usd > 0 ? Math.min(view.spend_usd / view.cap_usd, 1) : 0;
  const near = view.cap_usd > 0 && spendShare >= 0.75;

  return (
    <section className={cn("space-y-3 rounded-xl border p-4",
                           view.work_paused ? "border-step-todo bg-step-todo/10" : "border-white/10 bg-[#16181d]")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">
            {view.work_paused ? "Paid work is paused" : "Paid work is running normally"}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {view.work_paused
              ? `${view.reason ?? "no reason recorded"} — ${view.changed_by ?? "someone"}, ${
                  view.changed_at ? new Date(view.changed_at).toLocaleString() : "at some point"}`
              : "Pausing stops new runs and asks everything running to stop at its next checkpoint. Sign-in, results and billing stay up."}
          </p>
        </div>
        {view.work_paused ? (
          <Button size="sm" disabled={set.isPending} onClick={() => set.mutate({ paused: false })}>
            Let work start again
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Input className="w-56" placeholder="Why are you pausing?" value={reason}
                   onChange={(e) => setReason(e.target.value)} />
            <Button size="sm" variant="destructive" disabled={reason.trim().length < 3 || set.isPending}
                    onClick={() => set.mutate({ paused: true, reason: reason.trim() },
                                              { onSuccess: () => setReason("") })}>
              Pause everything
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-muted-foreground">
            Last 24 hours at Anthropic, before markup
            {view.org_cap_usd > 0 && ` · ${money(view.org_cap_usd)} ceiling per organisation`}
          </span>
          <span className={cn("tabular-nums", near && "text-step-todo")}>
            {money(view.spend_usd)}{view.cap_usd > 0 ? ` of ${money(view.cap_usd)}` : " (no ceiling set)"}
          </span>
        </div>
        {view.cap_usd > 0 && (
          <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
            <div className={cn("h-full rounded-full", near ? "bg-step-todo" : "bg-step-done")}
                 style={{ width: `${Math.max(spendShare * 100, 1)}%` }} />
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Deleting an organisation, when someone has asked us to.
 *
 * Shows what will go before anything goes, and lists the members so you can choose whose account
 * goes with it - someone who belongs to another workspace keeps theirs, and is ticked off by
 * default. The name has to be typed out: that is the whole protection against deleting the wrong
 * one, and it is worth more than a confirmation nobody reads.
 */
function DeleteDialog({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const preview = useDeletionPreview(orgId);
  const remove = useDeleteOrg();
  const [typed, setTyped] = useState("");
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const view = preview.data;

  // Default: anybody left with no other workspace, since deleting their organisation while keeping
  // their address and a working sign-in is not what was asked for.
  const picked = chosen ?? new Set((view?.members ?? []).filter((m) => m.other_orgs === 0)
                                                        .map((m) => m.user_id));
  const toggle = (id: string) => {
    const next = new Set(picked);
    if (!next.delete(id)) next.add(id);
    setChosen(next);
  };

  const going = Object.entries(view?.counts ?? {}).filter(([, n]) => n > 0);
  const ready = !!view && typed.trim() === view.name;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[85vh] w-full max-w-lg space-y-3 overflow-y-auto rounded-xl border border-white/10 bg-[#16181d] p-4">
        <h2 className="font-medium">Delete {view?.name ?? "this organisation"}</h2>
        {!view ? (
          <p className="text-sm text-muted-foreground">
            {preview.isLoading ? "Looking…" : "This organisation cannot be deleted."}
          </p>
        ) : (
          <>
            <div className="space-y-1 text-sm">
              <p className="text-muted-foreground">This destroys, and cannot be undone:</p>
              <ul className="text-xs text-muted-foreground">
                {going.length === 0 && <li>no content at all</li>}
                {going.map(([table, n]) => <li key={table}>{n} {table.replace(/_/g, " ")}</li>)}
              </ul>
            </div>

            <div className="space-y-1 text-sm">
              <p className="text-muted-foreground">Members — tick anyone whose account should go too:</p>
              {view.members.map((m) => (
                <label key={m.user_id} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={picked.has(m.user_id)}
                         onChange={() => toggle(m.user_id)} />
                  <span>{m.email}</span>
                  <span className="text-muted-foreground">
                    {m.role}
                    {m.other_orgs > 0
                      ? ` · in ${m.other_orgs} other workspace${m.other_orgs > 1 ? "s" : ""}, so their account is kept`
                      : " · nowhere else"}
                  </span>
                </label>
              ))}
              {view.members.length === 0 && <p className="text-xs text-muted-foreground">nobody</p>}
            </div>

            <Alert>
              <AlertDescription className="text-xs">
                {view.ledger_entries} billing record{view.ledger_entries === 1 ? "" : "s"} and any
                invoices are kept, as the law requires — the organisation stays as a name-less
                tombstone. Everything else above is gone for good.
              </AlertDescription>
            </Alert>

            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                Type <span className="text-foreground">{view.name}</span> to confirm:
              </p>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={view.name} />
            </div>
          </>
        )}
        {remove.isError && (
          <Alert variant="destructive">
            <AlertDescription>Nothing was deleted. Check the name and try again.</AlertDescription>
          </Alert>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={!ready || remove.isPending}
            onClick={() => remove.mutate(
              { orgId, confirmName: typed.trim(), deleteUserIds: [...picked] },
              { onSuccess: onClose },
            )}
          >
            Delete for good
          </Button>
        </div>
      </div>
    </div>
  );
}
